import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { getApiBase } from "../apiBase";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSearch, FiClock, FiMessageSquare, FiTrash2, FiDownload,
  FiRefreshCw, FiX, FiUser, FiCheck, FiMail, FiPhone, FiChevronLeft
} from "react-icons/fi";
import { HiSparkles, HiChatAlt2 } from "react-icons/hi";

const MonitorConversations = () => {
  const { tenantId, botId } = useParams();

  const [dateRange, setDateRange] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ start: null, end: null });
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [previewMap, setPreviewMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [userDataMap, setUserDataMap] = useState({});

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ tenant_id: tenantId, bot_id: botId });
      const res = await fetch(`${getApiBase()}/sessions/list?${params}`);
      const json = await res.json();
      const uniqueSessions = Array.from(
        new Map((json.sessions || []).map(s => [s.session_id, s])).values()
      );
      setSessions(uniqueSessions);
    } catch (err) {
      console.error(err);
      setError("Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  const openSession = async (session) => {
    setSelectedSession({ ...session, loading: true, data: null, error: null });
    try {
      const res = await fetch(
        `${getApiBase()}/sessions/${session.session_id}/stats?tenant_id=${tenantId}&bot_id=${botId}`
      );
      if (!res.ok) throw new Error("Failed to fetch details");
      const data = await res.json();
      const lastTurn = data.turns?.[data.turns.length - 1];
      const preview = lastTurn?.conversation?.bot_response || lastTurn?.conversation?.user_query || "No messages";
      setPreviewMap(prev => ({ ...prev, [session.session_id]: preview }));
      setSelectedSession(prev => ({ ...prev, loading: false, data, turns: data.turns || [] }));
    } catch (err) {
      setSelectedSession(prev => ({ ...prev, loading: false, error: "Could not load conversation details", data: { turns: [] } }));
    }
  };

  const deleteSession = async (sessionId, e) => {
    if (e) e.stopPropagation();
    setDeletingId(sessionId);
    try {
      await fetch(`${getApiBase()}/sessions/${sessionId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: tenantId, bot_id: botId })
      });
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (selectedSession?.session_id === sessionId) setSelectedSession(null);
      setToast("Conversation deleted");
      setTimeout(() => setToast(null), 2000);
    } catch {
      setToast("Failed to delete");
      setTimeout(() => setToast(null), 2000);
    } finally {
      setDeletingId(null);
    }
  };

  const exportConversation = (session, e) => {
    if (e) e.stopPropagation();
    const turns = selectedSession?.data?.turns || session.data?.turns || [];
    const userData = userDataMap[session.session_id];
    const userName = userData?.name || session.user_name || "User";
    const content = turns.map(turn => {
      const userQ = turn.conversation?.user_query || "";
      const botA = turn.conversation?.bot_response || "";
      const time = new Date(turn.timestamp || session.created_at).toLocaleString();
      return `[${time}]\n${userName}: ${userQ}\n\nDroog Assist: ${botA}\n\n${"=".repeat(50)}\n`;
    }).join("\n");
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `conversation-${userName.replace(/\s+/g, '_').toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setToast("Conversation exported");
    setTimeout(() => setToast(null), 2000);
  };

  useEffect(() => { fetchSessions(); }, [tenantId, botId]);

  const filteredSessions = useMemo(() => {
    let result = sessions;
    if (dateRange !== "all" && dateRange !== "custom") {
      const end = new Date(); end.setHours(23, 59, 59, 999);
      let start = new Date();
      if (dateRange === "today") { start.setHours(0, 0, 0, 0); }
      else if (dateRange === "7days") { start.setDate(start.getDate() - 6); start.setHours(0, 0, 0, 0); }
      else if (dateRange === "month") { start.setMonth(start.getMonth() - 1); start.setHours(0, 0, 0, 0); }
      result = result.filter(s => { const d = new Date(s.last_activity_at || s.created_at); return d >= start && d <= end; });
    } else if (dateRange === "custom" && customDateRange.start && customDateRange.end) {
      const start = new Date(customDateRange.start);
      const end = new Date(customDateRange.end); end.setHours(23, 59, 59, 999);
      result = result.filter(s => { const d = new Date(s.last_activity_at || s.created_at); return d >= start && d <= end; });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s => {
        const ud = userDataMap[s.session_id] || {};
        return (s.user_name || ud.name || "").toLowerCase().includes(q) ||
          (s.user_email || ud.email || "").toLowerCase().includes(q) ||
          (s.user_phone || ud.phone || "").toLowerCase().includes(q) ||
          (s.last_message || "").toLowerCase().includes(q) ||
          (s.conversation_summary || "").toLowerCase().includes(q);
      });
    }
    return result.sort((a, b) => new Date(b.last_activity_at || b.created_at) - new Date(a.last_activity_at || a.created_at));
  }, [sessions, dateRange, customDateRange, searchQuery, userDataMap]);

  const getUserContactInfo = (session) => {
    const ud = userDataMap[session.session_id] || {};
    return { name: session.user_name || ud.name || "Anonymous", email: session.user_email || ud.email, phone: session.user_phone || ud.phone };
  };

  const relatedSessions = useMemo(() => {
    if (!selectedSession) return [];
    const c = getUserContactInfo(selectedSession);
    if (!c.email && !c.phone) return [];
    return sessions.filter(s => {
      const sc = getUserContactInfo(s);   // <-- removed the `if (s.session_id === selectedSession.session_id) return false;` line
      return (c.email && sc.email === c.email) || (c.phone && sc.phone === c.phone);
    }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [selectedSession, sessions, userDataMap]);

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const getLastMessagePreview = (session) => {
    const msg = previewMap[session.session_id] || session.last_message || "No messages";
    return msg.length > 55 ? msg.slice(0, 55) + "…" : msg;
  };

  const getStatusBadge = (status) => {
    const s = (status || "").toLowerCase();
    const map = {
      active: { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", label: "Active" },
      ended: { bg: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-500", label: "Completed" },
      completed: { bg: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-500", label: "Completed" },
      abandoned: { bg: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500", label: "Abandoned" },
    };
    const style = map[s] || { bg: "bg-gray-50 text-gray-500 border-gray-200", dot: "bg-gray-400", label: "Unknown" };
    return (
      <span className={`inline-flex items-center gap-1 text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border ${style.bg}`}>
        <span className={`w-1 h-1 rounded-full ${style.dot}`} />
        {style.label}
      </span>
    );
  };

  return (
    <div className="h-screen w-full bg-[#F4F2F7] flex overflow-hidden" style={{ fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif" }}>

      {/* LEFT PANEL — Session List */}
      <div className="w-[268px] flex flex-col border-r border-gray-200 bg-white flex-shrink-0 overflow-hidden">

        <div className="px-3 pt-3 pb-2 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-2.5">
            <h1 className="text-[13px] font-bold text-[#5B3A60] flex items-center gap-1.5 tracking-tight">
              <HiChatAlt2 className="text-[#B89D4F]" size={15} />
              Conversations
            </h1>
            <button onClick={fetchSessions} className="p-1 text-gray-400 hover:text-[#5B3A60] rounded-md hover:bg-gray-50 transition-colors">
              <FiRefreshCw size={12} />
            </button>
          </div>

          <div className="relative mb-2">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={11} />
            <input
              type="text"
              placeholder="Search…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-7 pr-2 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-[11px] focus:outline-none focus:ring-1 focus:ring-[#B89D4F]/40 focus:border-[#B89D4F] transition-all"
            />
          </div>

          <div className="flex gap-0.5 bg-gray-100 p-0.5 rounded-lg">
            {[{ key: "today", label: "Today" }, { key: "7days", label: "7d" }, { key: "month", label: "30d" }, { key: "all", label: "All" }].map(r => (
              <button
                key={r.key}
                onClick={() => { setDateRange(r.key); setCustomDateRange({ start: null, end: null }); }}
                className={`flex-1 py-1 text-[10px] font-semibold rounded-md transition-all duration-150 ${
                  dateRange === r.key ? "bg-white text-[#5B3A60] shadow-sm" : "text-gray-400 hover:text-gray-600"
                }`}
              >{r.label}</button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1 min-h-0">
          {loading ? (
            <div className="space-y-2 pt-1">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-3 animate-pulse">
                  <div className="h-2.5 bg-gray-200 rounded w-2/5 mb-2" />
                  <div className="h-2 bg-gray-100 rounded w-4/5" />
                </div>
              ))}
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-400 text-xs">
              <HiChatAlt2 size={20} className="mb-2 text-gray-200" />
              No conversations
            </div>
          ) : (
            filteredSessions.map(session => {
              const isSelected = selectedSession?.session_id === session.session_id;
              const contact = getUserContactInfo(session);
              return (
                <motion.div
                  key={session.session_id}
                  layout
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => openSession(session)}
                  className={`group relative p-2.5 rounded-xl border cursor-pointer transition-all duration-150 ${
                    isSelected
                      ? "bg-[#5B3A60]/[0.04] border-[#B89D4F]/50 shadow-sm"
                      : "bg-transparent border-transparent hover:bg-gray-50 hover:border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[11px] font-semibold truncate max-w-[110px] ${isSelected ? "text-[#5B3A60]" : "text-gray-700"}`}>
                      {contact.name}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {getStatusBadge(session.session_status)}
                      <button
                        onClick={(e) => deleteSession(session.session_id, e)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-300 hover:text-rose-400 transition-all"
                      >
                        {deletingId === session.session_id ? <FiRefreshCw size={10} className="animate-spin" /> : <FiTrash2 size={10} />}
                      </button>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 line-clamp-1 mb-1.5 leading-relaxed">
                    {getLastMessagePreview(session)}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] text-gray-400 flex items-center gap-1">
                      <FiMessageSquare size={8} />{session.total_turns || 0}
                    </span>
                    <span className="text-[9px] text-gray-400">
                      {formatDateTime(session.last_activity_at || session.created_at)}
                    </span>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </div>

      {/* CENTER PANEL — Conversation View */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {selectedSession ? (
          <>
            <div className="px-4 py-2.5 border-b border-gray-200 flex items-center justify-between bg-white flex-shrink-0">
              <div className="min-w-0 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedSession(null)}
                  className="md:hidden p-2 -ml-2 text-gray-500 hover:bg-gray-100 rounded-full flex-shrink-0"
                  aria-label="Back to session list"
                >
                  <FiChevronLeft size={20} />
                </button>
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#5B3A60] to-[#7A5280] flex items-center justify-center flex-shrink-0">
                  <FiUser size={11} className="text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-[12px] font-bold text-gray-900 truncate">{getUserContactInfo(selectedSession).name}</h2>
                    {getStatusBadge(selectedSession.session_status)}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {getUserContactInfo(selectedSession).email && (
                      <a href={`mailto:${getUserContactInfo(selectedSession).email}`} className="text-[10px] text-gray-400 flex items-center gap-1 hover:text-[#5B3A60] transition-colors truncate">
                        <FiMail size={9} />{getUserContactInfo(selectedSession).email}
                      </a>
                    )}
                    {getUserContactInfo(selectedSession).phone && (
                      <span className="text-[10px] text-gray-400 flex items-center gap-1">
                        <FiPhone size={9} />{getUserContactInfo(selectedSession).phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={(e) => exportConversation(selectedSession, e)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-semibold text-gray-600 bg-gray-50 hover:bg-[#5B3A60] hover:text-white rounded-lg transition-all border border-gray-200 hover:border-[#5B3A60]"
                >
                  <FiDownload size={11} />Export
                </button>
                {/* <button
                  onClick={(e) => deleteSession(selectedSession.session_id, e)}
                  disabled={deletingId === selectedSession.session_id}
                  className="p-1.5 text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                >
                  {deletingId === selectedSession.session_id ? <FiRefreshCw size={12} className="animate-spin" /> : <FiTrash2 size={12} />}
                </button> */}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto bg-[#F4F2F7]">
              <div className="max-w-2xl mx-auto px-4 py-4">
                {selectedSession.loading ? (
                  <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                    <div className="w-6 h-6 border-2 border-[#B89D4F] border-t-transparent rounded-full animate-spin mb-2" />
                    <span className="text-[11px]">Loading…</span>
                  </div>
                ) : selectedSession.error ? (
                  <div className="flex flex-col items-center justify-center h-40 text-rose-500 bg-rose-50 rounded-xl border border-rose-100">
                    <FiX size={18} className="mb-1.5" />
                    <span className="text-[11px] font-medium">{selectedSession.error}</span>
                  </div>
                ) : !selectedSession.data?.turns?.length ? (
                  <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                    <HiChatAlt2 size={22} className="mb-2 text-gray-300" />
                    <span className="text-[11px]">No messages recorded</span>
                  </div>
                ) : (
                  <div className="space-y-4 pb-4">
                    {selectedSession?.conversation_summary && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="relative rounded-xl border border-[#E7DFE9] bg-white overflow-hidden"
                      >
                        <div className="h-0.5 w-full bg-gradient-to-r from-[#5B3A60] via-[#B89D4F] to-[#5B3A60]" />
                        <div className="p-3 flex gap-3">
                          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#5B3A60] to-[#7A5280] flex items-center justify-center flex-shrink-0">
                            <HiSparkles className="text-white text-xs" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[9px] font-bold tracking-widest text-[#5B3A60] uppercase mb-1">AI Summary</p>
                            <p className="text-[11px] text-gray-600 leading-relaxed">{selectedSession.conversation_summary}</p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {selectedSession.data.turns.map((turn, idx) => (
                      <motion.div
                        key={turn.message_id || idx}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        className="space-y-2"
                      >
                        {turn.conversation?.user_query && (
                          <div className="flex justify-end">
                            <div className="max-w-[75%]">
                              <div className="bg-[#5B3A60] text-white rounded-2xl rounded-tr-sm px-3.5 py-2.5">
                                <p className="text-[12px] leading-relaxed whitespace-pre-wrap">{turn.conversation.user_query}</p>
                              </div>
                              <div className="flex justify-end items-center gap-1.5 mt-1">
                                <span className="text-[9px] text-gray-400">{formatDateTime(turn.timestamp)}</span>
                                <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center">
                                  <FiUser size={8} className="text-[#5B3A60]" />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {turn.conversation?.bot_response && (() => {
                          const isHumanAgent =
                            turn.conversation?.response_type === "human_agent_reply" ||
                            turn.conversation?.response_type === "human_agent";
                          const sourceLabel = isHumanAgent ? "Human agent" : "AI · RAG";
                          return (
                          <div className="flex justify-start gap-2">
                            {!isHumanAgent && (
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#B89D4F] to-[#D4B968] flex items-center justify-center flex-shrink-0 mt-0.5">
                                <HiSparkles className="text-white" size={9} />
                              </div>
                            )}
                            <div className="max-w-[75%]">
                              <div className={`rounded-2xl rounded-tl-sm px-3.5 py-2.5 shadow-sm ${
                                isHumanAgent
                                  ? "bg-[#5B3A60] text-white"
                                  : "bg-white border border-gray-100 text-gray-800"
                              }`}>
                                <span className={`inline-block text-[9px] font-semibold mb-1 px-1.5 py-0.5 rounded ${
                                  isHumanAgent ? "bg-white/20 text-white" : "bg-[#5B3A60]/10 text-[#5B3A60]"
                                }`}>
                                  {sourceLabel}
                                </span>
                                <p className="text-[12px] leading-relaxed whitespace-pre-wrap">{turn.conversation.bot_response}</p>
                              </div>
                              <div className="flex items-center gap-1.5 mt-1 ml-1">
                                <span className="text-[9px] text-gray-400">{isHumanAgent ? "Human agent" : "Droog Assist"} · {formatDateTime(turn.timestamp)}</span>
                              </div>
                            </div>
                          </div>
                          );
                        })()}
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 bg-[#F4F2F7]">
            <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-3 shadow-sm border border-gray-100">
              <HiChatAlt2 size={22} className="text-gray-300" />
            </div>
            <p className="text-[12px] font-semibold text-gray-500">Select a conversation</p>
            <p className="text-[11px] text-gray-400 mt-0.5">View details and history</p>
          </div>
        )}
      </div>

      {/* RIGHT PANEL — Interaction History */}
      <div className="w-[240px] flex flex-col border-l border-gray-200 bg-white flex-shrink-0 overflow-hidden">
        <div className="px-3 pt-3 pb-2 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-[11px] font-bold text-gray-700 uppercase tracking-widest flex items-center gap-1.5">
            <FiClock className="text-[#5B3A60]" size={11} />
            History
          </h3>
          <p className="text-[10px] text-gray-400 mt-0.5">
            {selectedSession ? "Past sessions with this contact" : "Select a conversation"}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1.5 min-h-0">
          {selectedSession ? (
            relatedSessions.length > 0 ? (
              relatedSessions.map((session, index) => {
  const isSelected = selectedSession?.session_id === session.session_id;
  return (
    <motion.div
      key={session.session_id}
      initial={{ opacity: 0, x: 8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04 }}
      onClick={() => openSession(session)}
      className={`p-2.5 rounded-xl transition-all cursor-pointer ${
        isSelected
          ? "bg-white border-2 border-[#B89D4F] shadow-sm"
          : "bg-gray-50 border border-gray-100 hover:border-[#B89D4F]/50 hover:bg-white hover:shadow-sm"
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold text-gray-700 flex items-center gap-1">
          <HiChatAlt2 className="text-[#5B3A60]" size={10} />
          {new Date(session.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
        </span>
        {getStatusBadge(session.session_status)}
      </div>
      <p className="text-[10px] text-gray-500 line-clamp-2 mb-1.5 leading-relaxed">
        {session.conversation_summary || "No summary available"}
      </p>
      <div className="flex items-center justify-between text-[9px] text-gray-400 font-medium">
        <span>{session.total_turns || 0} msgs</span>
        <span>{formatDateTime(session.last_activity_at || session.created_at)}</span>
      </div>
    </motion.div>
  );
})
            ) : (
              <div className="text-center py-8 text-gray-400 text-[11px] px-3">
                No past interactions found.
              </div>
            )
          ) : (
            <div className="text-center py-8 text-gray-400 text-[11px] px-3">
              Select a conversation to view history.
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            className="fixed bottom-6 right-6 px-4 py-2.5 rounded-xl bg-[#5B3A60] text-white text-[11px] font-semibold shadow-xl z-[3000] flex items-center gap-2 border border-white/10"
          >
            <div className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <FiCheck size={9} className="text-emerald-300" />
            </div>
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MonitorConversations;